import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  Edit3,
  Archive,
  ArrowLeft,
  ArrowRight,
  Check,
  CalendarClock,
  UserCircle2,
  Save,
  X,
  Loader2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { toast } from 'sonner';

import { ActionButton, SectionHeader } from './VisualAtoms';
import {
  FLOW_STAGE_TONE,
  FLOW_STAGE_VARIANT,
  toneForVariant,
  humanize,
} from '../theme/adminVisualSystem';
import { IdeaScoreBadge, MiniMetersRow, MetricRing } from './ScoreVisuals';
import AnalysisBlocks from './AnalysisBlocks';

/**
 * FlowItemCard — compact-by-default with expandable detail panel.
 *
 * Layout:
 *   [score badge]  [title + meta strip]  [expand toggle]
 *   compact summary preview (2 lines)
 *   mini Hustle/Gains/Cost bars
 *   per-stage primary action row
 *
 * Expanded:
 *   - score rings (Hustle/Gains/Cost)
 *   - structured AI analysis blocks
 *   - structured summary, raw input
 *   - inline editor (core / score / todo) per stage
 *   - full action footer
 */
const STAGE_LABEL = { ideas: 'Idea', in_review: 'Reviewing', todo: 'To Do' };

const PRIORITY_VARIANT = {
  low: 'ghost',
  medium: 'blue',
  high: 'warning',
  critical: 'danger',
};

function StageDot({ stage }) {
  const tone = FLOW_STAGE_TONE[stage] || FLOW_STAGE_TONE.ideas;
  return (
    <motion.span
      aria-hidden
      className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
      style={{ background: tone.text, boxShadow: `0 0 8px ${tone.glow}` }}
      animate={{ opacity: [0.55, 1, 0.55] }}
      transition={{ duration: 2.4, repeat: Infinity }}
    />
  );
}

function PriorityBadge({ priority }) {
  if (!priority) return null;
  const v = PRIORITY_VARIANT[priority] || 'ghost';
  const tone = toneForVariant(v);
  return (
    <span
      className="text-[9px] font-mono px-1.5 py-px border uppercase tracking-[0.15em]"
      style={{ color: tone.text, borderColor: tone.border }}
    >
      {humanize(priority)}
    </span>
  );
}

export default function FlowItemCard({ item, onUpdate, onMove, onArchive, onComplete, onAi }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(null); // null | 'core' | 'score' | 'todo'
  const [aiBusy, setAiBusy] = useState(null); // null | 'structure' | 'score'

  // Local working drafts populated when an editor opens
  const [coreDraft, setCoreDraft] = useState({});
  const [scoreDraft, setScoreDraft] = useState({});
  const [todoDraft, setTodoDraft] = useState({});

  const stageTone = FLOW_STAGE_TONE[item.stage] || FLOW_STAGE_TONE.ideas;

  const aiParsed = (() => {
    if (!item.ai_notes) return null;
    try {
      const obj = JSON.parse(item.ai_notes);
      return typeof obj === 'object' && obj ? obj : null;
    } catch {
      return null;
    }
  })();

  const startEdit = (mode) => {
    setEditing(mode);
    setExpanded(true);
    setCoreDraft({
      title: item.title || '',
      raw_input: item.raw_input || '',
      description: item.description || '',
      category: item.category || '',
      priority: item.priority || '',
    });
    setScoreDraft({
      hustle_score: item.hustle_score ?? '',
      gains_score: item.gains_score ?? '',
      cost_score: item.cost_score ?? '',
      hustle_weight: item.hustle_weight ?? 0.3,
      gains_weight: item.gains_weight ?? 0.5,
      cost_weight: item.cost_weight ?? 0.2,
      review_notes: item.review_notes || '',
    });
    setTodoDraft({
      todo_notes: item.todo_notes || '',
      assigned_to: item.assigned_to || '',
      due_date: item.due_date || '',
      priority: item.priority || '',
    });
  };

  const cancelEdit = () => setEditing(null);

  const livePreviewScore = (() => {
    const { hustle_score, gains_score, cost_score, hustle_weight, gains_weight, cost_weight } = scoreDraft;
    if (hustle_score === '' || gains_score === '' || cost_score === '') return null;
    return (
      Number(hustle_score) * Number(hustle_weight) +
      Number(gains_score) * Number(gains_weight) -
      Number(cost_score) * Number(cost_weight)
    );
  })();

  const saveCore = async () => {
    await onUpdate(item.id, {
      title: coreDraft.title || null,
      raw_input: coreDraft.raw_input || null,
      description: coreDraft.description || null,
      category: coreDraft.category || null,
      priority: coreDraft.priority || null,
    });
    setEditing(null);
  };

  const saveScore = async () => {
    const body = {};
    ['hustle_score', 'gains_score', 'cost_score'].forEach((k) => {
      if (scoreDraft[k] !== '' && scoreDraft[k] != null) body[k] = Number(scoreDraft[k]);
    });
    ['hustle_weight', 'gains_weight', 'cost_weight'].forEach((k) => {
      if (scoreDraft[k] !== '' && scoreDraft[k] != null) body[k] = Number(scoreDraft[k]);
    });
    if (scoreDraft.review_notes) body.review_notes = scoreDraft.review_notes;
    await onUpdate(item.id, body);
    setEditing(null);
  };

  const saveTodo = async () => {
    await onUpdate(item.id, {
      todo_notes: todoDraft.todo_notes || null,
      assigned_to: todoDraft.assigned_to || null,
      due_date: todoDraft.due_date || null,
      priority: todoDraft.priority || null,
    });
    setEditing(null);
  };

  const runAi = async (kind) => {
    if (!onAi) return;
    setAiBusy(kind);
    try {
      await onAi(item.id, kind);
      setExpanded(true);
    } finally {
      setAiBusy(null);
    }
  };

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}
      data-testid={`flow-card-${item.id}`}
      className="relative border border-[#1a1a1a] bg-[#0d0d0d] hover:border-[#90E0EF]/25 transition-colors duration-300"
      style={{ borderLeft: `2px solid ${stageTone.text}` }}
    >
      {/* COMPACT HEAD — always visible */}
      <div className="p-3.5 sm:p-4">
        <div className="flex items-start gap-3">
          <IdeaScoreBadge score={item.idea_score} size={48} label={false} testId={`score-${item.id}`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <StageDot stage={item.stage} />
              <span className="text-[9px] font-mono text-[#666] tracking-[0.18em] uppercase">
                {STAGE_LABEL[item.stage]}
              </span>
              {item.category && (
                <span className="text-[9px] font-mono text-[#888] tracking-[0.12em] uppercase">
                  · {humanize(item.category)}
                </span>
              )}
              <PriorityBadge priority={item.priority} />
              <span className="text-[9px] font-mono text-[#666] uppercase tracking-[0.15em] ml-auto">
                AI · {humanize(item.ai_status || 'manual')}
              </span>
            </div>
            <h3 className="font-outfit text-base font-semibold text-white tracking-tight mt-1 break-words line-clamp-1">
              {item.title}
            </h3>
            {(item.structured_summary || item.raw_input) && !expanded && (
              <p className="text-[12px] font-mono text-[#aaa] mt-1.5 leading-relaxed line-clamp-2">
                {item.structured_summary || item.raw_input}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-clickable="true"
            data-testid={`expand-toggle-${item.id}`}
            aria-label={expanded ? 'Collapse' : 'Expand'}
            className="w-7 h-7 border border-[#222] text-[#888] hover:text-white hover:border-[#90E0EF]/40 flex items-center justify-center transition-colors shrink-0"
          >
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        </div>

        {/* Mini metrics — only on Ideas/In Review (todo stage hides) */}
        {item.stage !== 'todo' && (
          <div className="mt-3.5">
            <MiniMetersRow
              hustle={item.hustle_score}
              gains={item.gains_score}
              cost={item.cost_score}
            />
          </div>
        )}

        {/* TO-DO compact metadata strip */}
        {item.stage === 'todo' && (
          <div className="grid grid-cols-2 gap-2 mt-3 text-[11px] font-mono">
            <div className="flex items-center gap-1.5 text-[#aaa] truncate">
              <UserCircle2 className="w-3 h-3 text-[#90E0EF]/70 shrink-0" />
              <span className="truncate">{item.assigned_to || <span className="text-[#444]">— unassigned</span>}</span>
            </div>
            <div className="flex items-center gap-1.5 text-[#aaa] truncate">
              <CalendarClock className="w-3 h-3 text-[#90E0EF]/70 shrink-0" />
              <span className="truncate">{item.due_date || <span className="text-[#444]">— no due date</span>}</span>
            </div>
          </div>
        )}

        {/* Compact action row — primary buttons per stage */}
        <div className="flex flex-wrap gap-1.5 mt-3.5">
          {item.stage === 'ideas' && (
            <>
              <ActionButton
                variant={FLOW_STAGE_VARIANT.in_review}
                icon={ArrowRight}
                onClick={() => onMove(item.id, 'in_review')}
                dense
                data-testid={`move-in_review-${item.id}`}
              >
                Move to In Review
              </ActionButton>
              <ActionButton
                variant="purple"
                icon={aiBusy === 'structure' ? Loader2 : Sparkles}
                onClick={() => runAi('structure')}
                disabled={aiBusy !== null}
                dense
                data-testid={`ai-structure-${item.id}`}
              >
                {aiBusy === 'structure' ? 'Structuring…' : 'Structure with AI'}
              </ActionButton>
            </>
          )}
          {item.stage === 'in_review' && (
            <>
              <ActionButton
                variant={FLOW_STAGE_VARIANT.todo}
                icon={ArrowRight}
                onClick={() => onMove(item.id, 'todo')}
                dense
                data-testid={`move-todo-${item.id}`}
              >
                Move to To Do
              </ActionButton>
              <ActionButton
                variant="purple"
                icon={aiBusy === 'score' ? Loader2 : Sparkles}
                onClick={() => runAi('score')}
                disabled={aiBusy !== null}
                dense
                data-testid={`ai-score-${item.id}`}
              >
                {aiBusy === 'score' ? 'Scoring…' : 'Score with AI'}
              </ActionButton>
            </>
          )}
          {item.stage === 'todo' && (
            <>
              <ActionButton
                variant="success"
                icon={Check}
                onClick={() => onComplete(item.id)}
                dense
                data-testid={`complete-${item.id}`}
              >
                Mark Done
              </ActionButton>
              <ActionButton
                variant={FLOW_STAGE_VARIANT.in_review}
                icon={ArrowLeft}
                onClick={() => onMove(item.id, 'in_review')}
                dense
              >
                Back to Review
              </ActionButton>
            </>
          )}
          <ActionButton
            variant="ghost"
            icon={expanded ? ChevronUp : ChevronDown}
            onClick={() => setExpanded((v) => !v)}
            dense
            className="ml-auto"
          >
            {expanded ? 'Collapse' : 'Expand'}
          </ActionButton>
        </div>
      </div>

      {/* EXPANDED PANEL */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="expanded"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}
            className="overflow-hidden border-t border-[#141414]"
            data-testid={`expanded-${item.id}`}
          >
            <div className="p-4 sm:p-5 space-y-5">
              {/* Score rings — only on Ideas / In Review */}
              {item.stage !== 'todo' && (
                <section>
                  <SectionHeader title="Score breakdown" helper="Hustle = effort. Gains = reward. Cost = financial weight." />
                  <div className="flex justify-around items-start mt-3 gap-3 flex-wrap">
                    <MetricRing label="Hustle" value={item.hustle_score} accent="#e4c07a" />
                    <MetricRing label="Gains" value={item.gains_score} accent="#7ed4a0" />
                    <MetricRing label="Cost" value={item.cost_score} accent="#e88ba0" />
                  </div>
                </section>
              )}

              {/* Structured + raw text */}
              {(item.structured_summary || item.raw_input || item.description) && editing == null && (
                <section className="space-y-3">
                  {item.structured_summary && (
                    <div>
                      <SectionHeader title="Structured summary" />
                      <p className="text-[13px] font-mono text-[#d6d6d6] whitespace-pre-wrap leading-relaxed mt-2">
                        {item.structured_summary}
                      </p>
                    </div>
                  )}
                  {item.description && item.description !== item.structured_summary && (
                    <div>
                      <SectionHeader title="Description" />
                      <p className="text-[13px] font-mono text-[#aaa] whitespace-pre-wrap leading-relaxed mt-2">
                        {item.description}
                      </p>
                    </div>
                  )}
                  {item.raw_input && (
                    <div>
                      <SectionHeader title="Raw input" helper="The original idea text." />
                      <p className="text-[12px] font-mono text-[#888] whitespace-pre-wrap leading-relaxed mt-2">
                        {item.raw_input}
                      </p>
                    </div>
                  )}
                </section>
              )}

              {/* AI / curator analysis blocks */}
              {(aiParsed || item.review_notes) && editing == null && (
                <section>
                  <SectionHeader title="Analysis" helper="AI insights and curator notes — color-coded for quick scanning." />
                  <div className="mt-3">
                    <AnalysisBlocks
                      ai={aiParsed}
                      curatorNotes={item.review_notes}
                      currentStage={item.stage}
                    />
                  </div>
                </section>
              )}

              {item.ai_status === 'ai_failed' && editing == null && (
                <p className="text-[11px] font-mono text-red-300/80 leading-relaxed">
                  AI processing failed. You can still edit this idea manually, or retry.
                </p>
              )}

              {/* Inline editors */}
              {editing === 'core' && (
                <section className="space-y-2" data-testid={`core-editor-${item.id}`}>
                  <SectionHeader title="Edit idea" />
                  <input
                    value={coreDraft.title}
                    onChange={(e) => setCoreDraft({ ...coreDraft, title: e.target.value })}
                    placeholder="Title"
                    className="w-full bg-transparent border border-[#222] text-white font-mono text-xs p-2 focus:outline-none focus:border-[#90E0EF]"
                  />
                  <textarea
                    value={coreDraft.raw_input}
                    onChange={(e) => setCoreDraft({ ...coreDraft, raw_input: e.target.value })}
                    placeholder="Raw idea text…"
                    rows={3}
                    className="w-full bg-transparent border border-[#222] text-white font-mono text-xs p-2 focus:outline-none focus:border-[#90E0EF] resize-none"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      value={coreDraft.category}
                      onChange={(e) => setCoreDraft({ ...coreDraft, category: e.target.value })}
                      placeholder="Category (e.g. installation)"
                      className="bg-transparent border border-[#222] text-white font-mono text-xs p-2 focus:outline-none focus:border-[#90E0EF]"
                    />
                    <select
                      value={coreDraft.priority}
                      onChange={(e) => setCoreDraft({ ...coreDraft, priority: e.target.value })}
                      className="bg-[#0a0a0a] border border-[#222] text-white font-mono text-xs p-2 focus:outline-none focus:border-[#90E0EF]"
                    >
                      <option value="">Priority</option>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <ActionButton variant="primary" onClick={saveCore} icon={Save} dense data-testid={`save-core-${item.id}`}>Save</ActionButton>
                    <ActionButton variant="secondary" onClick={cancelEdit} icon={X} dense>Cancel</ActionButton>
                  </div>
                </section>
              )}

              {editing === 'score' && (
                <section className="space-y-3" data-testid={`score-editor-${item.id}`}>
                  <SectionHeader title="Score this idea" helper="Hustle = effort. Gains = potential reward. Cost = financial weight." />
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { key: 'hustle_score', wkey: 'hustle_weight', label: 'Hustle' },
                      { key: 'gains_score', wkey: 'gains_weight', label: 'Gains' },
                      { key: 'cost_score', wkey: 'cost_weight', label: 'Cost' },
                    ].map((f) => (
                      <div key={f.key} className="border border-[#1a1a1a] bg-[#0a0a0a] p-2">
                        <p className="text-[9px] font-mono text-[#666] tracking-[0.15em] uppercase">{f.label} 1–10</p>
                        <input
                          type="number"
                          min={1}
                          max={10}
                          value={scoreDraft[f.key]}
                          onChange={(e) => setScoreDraft({ ...scoreDraft, [f.key]: e.target.value })}
                          data-testid={`score-input-${f.key}-${item.id}`}
                          className="w-full bg-transparent text-white font-outfit text-lg font-semibold focus:outline-none"
                        />
                        <input
                          type="number"
                          min={0}
                          max={1}
                          step={0.05}
                          value={scoreDraft[f.wkey]}
                          onChange={(e) => setScoreDraft({ ...scoreDraft, [f.wkey]: e.target.value })}
                          className="w-full bg-transparent text-[#888] font-mono text-[10px] focus:outline-none mt-1 border-t border-[#1a1a1a] pt-1"
                          title="Weight (0..1)"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="text-[10px] font-mono text-[#666] tracking-[0.15em] uppercase">Live score</p>
                    <IdeaScoreBadge score={livePreviewScore} size={44} label={false} />
                  </div>
                  <textarea
                    value={scoreDraft.review_notes}
                    onChange={(e) => setScoreDraft({ ...scoreDraft, review_notes: e.target.value })}
                    placeholder="Curator review notes — concerns, founder-market fit, hidden costs…"
                    rows={3}
                    data-testid={`review-notes-${item.id}`}
                    className="w-full bg-transparent border border-[#222] text-white font-mono text-xs p-2 focus:outline-none focus:border-[#90E0EF] resize-none"
                  />
                  <div className="flex gap-2 flex-wrap">
                    <ActionButton variant="primary" onClick={saveScore} icon={Save} dense data-testid={`save-score-${item.id}`}>Save</ActionButton>
                    <ActionButton variant="secondary" onClick={cancelEdit} icon={X} dense>Cancel</ActionButton>
                    <ActionButton
                      variant="purple"
                      onClick={() => runAi('score')}
                      icon={aiBusy === 'score' ? Loader2 : Sparkles}
                      disabled={aiBusy !== null}
                      dense
                    >
                      {aiBusy === 'score' ? 'Scoring…' : 'Score with AI'}
                    </ActionButton>
                  </div>
                </section>
              )}

              {editing === 'todo' && (
                <section className="space-y-2" data-testid={`todo-editor-${item.id}`}>
                  <SectionHeader title="Edit task" />
                  <input
                    value={todoDraft.assigned_to}
                    onChange={(e) => setTodoDraft({ ...todoDraft, assigned_to: e.target.value })}
                    placeholder="Assigned to"
                    className="w-full bg-transparent border border-[#222] text-white font-mono text-xs p-2 focus:outline-none focus:border-[#90E0EF]"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="date"
                      value={todoDraft.due_date}
                      onChange={(e) => setTodoDraft({ ...todoDraft, due_date: e.target.value })}
                      className="bg-transparent border border-[#222] text-white font-mono text-xs p-2 focus:outline-none focus:border-[#90E0EF]"
                    />
                    <select
                      value={todoDraft.priority}
                      onChange={(e) => setTodoDraft({ ...todoDraft, priority: e.target.value })}
                      className="bg-[#0a0a0a] border border-[#222] text-white font-mono text-xs p-2 focus:outline-none focus:border-[#90E0EF]"
                    >
                      <option value="">Priority</option>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                  <textarea
                    value={todoDraft.todo_notes}
                    onChange={(e) => setTodoDraft({ ...todoDraft, todo_notes: e.target.value })}
                    placeholder="Execution notes…"
                    rows={3}
                    className="w-full bg-transparent border border-[#222] text-white font-mono text-xs p-2 focus:outline-none focus:border-[#90E0EF] resize-none"
                  />
                  <div className="flex gap-2">
                    <ActionButton variant="primary" onClick={saveTodo} icon={Save} dense data-testid={`save-todo-${item.id}`}>Save</ActionButton>
                    <ActionButton variant="secondary" onClick={cancelEdit} icon={X} dense>Cancel</ActionButton>
                  </div>
                </section>
              )}

              {/* Full action footer (only when not in an editor) */}
              {editing == null && (
                <div className="flex flex-wrap gap-1.5 pt-3 border-t border-[#141414]">
                  {item.stage === 'ideas' && (
                    <ActionButton variant="secondary" icon={Edit3} onClick={() => startEdit('core')} dense>
                      Edit
                    </ActionButton>
                  )}
                  {item.stage === 'in_review' && (
                    <>
                      <ActionButton
                        variant={FLOW_STAGE_VARIANT.ideas}
                        icon={ArrowLeft}
                        onClick={() => onMove(item.id, 'ideas')}
                        dense
                      >
                        Back to Ideas
                      </ActionButton>
                      <ActionButton variant="secondary" icon={Edit3} onClick={() => startEdit('score')} dense data-testid={`edit-score-${item.id}`}>
                        Edit Scores
                      </ActionButton>
                      <ActionButton variant="secondary" icon={Edit3} onClick={() => startEdit('core')} dense>
                        Edit
                      </ActionButton>
                    </>
                  )}
                  {item.stage === 'todo' && (
                    <ActionButton variant="secondary" icon={Edit3} onClick={() => startEdit('todo')} dense data-testid={`edit-todo-${item.id}`}>
                      Edit Task
                    </ActionButton>
                  )}
                  <ActionButton
                    variant="ghost"
                    icon={Archive}
                    onClick={() => onArchive(item.id)}
                    dense
                    data-testid={`archive-${item.id}`}
                    className="ml-auto"
                  >
                    Archive
                  </ActionButton>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.article>
  );
}
